<template>
  <button
    :aria-label="label"
    :class="joinClasses('issue-field__menu', modifiers)"
    type="button"
    @click="newFile"
  >
    <slot />
    <PlusIcon
      v-if="!$slots.default"
      class="icon icon--small"
    />
    <input
      :id="`${fileType}-upload`"
      ref="fileInput"
      :accept="acceptedFileTypes"
      class="hide"
      :multiple="multiple"
      type="file"
      @change="addFiles"
    >
  </button>
</template>

<script setup>
import { ref } from 'vue';
import { v4 as uuid } from 'uuid';
import useUploader from '@/vendor/use_uploader';
import PlusIcon from '@/images/plus-16.svg';
import joinClasses from '@/functions/join_classes';

const emit = defineEmits(['updateReader', 'updateBlob', 'updateError']);

const props = defineProps({
  modifiers: { type: Array, default: () => [] },
  label: { type: String, required: true },
  multiple: { type: Boolean, default: true },
  acceptedFileTypes: { type: Array, default: () => [] },
  fileType: {
    type: String,
    required: true,
    default: 'image',
    validator: (value) => ['image', 'attachment'].includes(value),
  },
});

const fileInput = ref(null);
const uploader = useUploader();

function newFile() {
  fileInput.value.focus();
  fileInput.value.click();
}

function addFiles({ target: { files } }) {
  if (!files || files.length <= 0) return;
  props.fileType === 'image' && Object.values(files).forEach((image) => addImage(image));
  props.fileType === 'attachment' && Object.values(files).forEach((attachment) => addAttachment(attachment));
}

async function addImage(file) {
  if (!file) return;
  let newImage = {
    isNew: true,
    id: uuid(),
    image_blob: { filename: file.name, content_type: file.type },
    created_at: new Date(),
    attached: true,
  };

  if (props.acceptedFileTypes.length > 0 && !props.acceptedFileTypes.includes(newImage.image_blob.content_type)) {
    emit('updateError', { filename: newImage.image_blob.filename });
  } else {
    emit('updateError', {});
    let reader = new FileReader();
    reader.onload = () => {
      newImage.url = reader.result;
      emit('updateReader', newImage);
    };
    reader.readAsDataURL(file);

    newImage.upload = uploader.create(file);
    emit('updateBlob', newImage);
  }
}

async function addAttachment(file) {
  if (!file) return;

  let newAttachment = {
    isNew: true,
    id: uuid(),
    filename: file.name,
    content_type: file.type,
    file_blob: { filename: file.name, content_type: file.type },
    created_at: new Date(),
    attached: true,
  };

  if (props.acceptedFileTypes.length > 0 && !props.acceptedFileTypes.includes(newAttachment.file_blob.content_type)) {
    emit('updateError', { filename: newAttachment.file_blob.filename });
  } else {
    emit('updateError', {});
    emit('updateReader', newAttachment);

    newAttachment.upload = uploader.create(file);
    emit('updateBlob', newAttachment);
  }
}
</script>
